import { useState, useRef } from 'react'
import {
  Box, Button, Checkbox, Chip, FormControlLabel,
  InputAdornment, Menu, MenuItem, Stack, TextField,
} from '@mui/material'
import * as Icons from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { ENTRY_TYPE_LABELS, ALL_ENTRY_TYPES } from './journal-utils'

export const JournalControls = ({
  activeTypes, onTypesChange,
  searchQuery, onSearchChange,
  dateFrom, onDateFromChange,
  dateTo, onDateToChange,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null)
  const buttonRef = useRef(null)

  const handleTypeToggle = (type) => {
    if (activeTypes.includes(type)) {
      if (activeTypes.length === 1) return
      onTypesChange(activeTypes.filter(t => t !== type))
    } else {
      onTypesChange([...activeTypes, type])
    }
  }

  const handleRemoveChip = (type) => {
    if (activeTypes.length === 1) return
    onTypesChange(activeTypes.filter(t => t !== type))
  }

  const allSelected = activeTypes.length === ALL_ENTRY_TYPES.length

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <Button
            ref={buttonRef}
            size="small"
            variant="outlined"
            endIcon={<Icons.FilterList fontSize="small" />}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{ flexShrink: 0 }}
          >
            Note Types
          </Button>

          <TextField
            size="small"
            placeholder="Search notes…"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{ flex: '1 1 180px', minWidth: 140 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icons.Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <Icons.Clear
                    fontSize="small"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => onSearchChange('')}
                  />
                </InputAdornment>
              ) : null,
            }}
          />

          <DatePicker
            label="From"
            value={dateFrom}
            onChange={onDateFromChange}
            maxDate={dateTo || undefined}
            slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
          />
          <DatePicker
            label="To"
            value={dateTo}
            onChange={onDateToChange}
            minDate={dateFrom || undefined}
            slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
          />
        </Stack>

        {!allSelected && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap">
            {activeTypes.map(type => (
              <Chip
                key={type}
                label={ENTRY_TYPE_LABELS[type]}
                size="small"
                onDelete={() => handleRemoveChip(type)}
              />
            ))}
          </Stack>
        )}
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {ALL_ENTRY_TYPES.map(type => (
          <MenuItem
            key={type}
            dense
            onClick={() => handleTypeToggle(type)}
            sx={{ gap: 0.5 }}
          >
            <Checkbox
              size="small"
              checked={activeTypes.includes(type)}
              disableRipple
              sx={{ p: 0.5 }}
            />
            <FormControlLabel
              label={ENTRY_TYPE_LABELS[type]}
              control={<span />}
              sx={{ m: 0 }}
            />
          </MenuItem>
        ))}
      </Menu>
    </LocalizationProvider>
  )
}
